<script setup lang="ts">
import { useToast, type ToastVariant } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'

const { t: uebersetze } = useI18n()

const { items, dismiss } = useToast()

const variantClass: Record<ToastVariant, string> = {
  info: 'border-line bg-surface text-fg',
  success: 'border-success/40 bg-success/10 text-success',
  error: 'border-red-400/60 bg-red-50 text-red-900 dark:bg-red-950/60 dark:text-red-200 dark:border-red-900',
}
</script>

<template>
  <div
    class="fixed z-50 bottom-4 right-4 flex flex-col gap-2 max-w-[min(420px,calc(100vw-2rem))]"
    role="region"
    :aria-label="uebersetze('ui.benachrichtigungen')"
    aria-live="polite"
  >
    <transition-group
      tag="div"
      class="flex flex-col gap-2"
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
    >
      <div
        v-for="t in items"
        :key="t.id"
        :class="[
          'flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
          'text-sm font-medium backdrop-blur',
          variantClass[t.variant],
        ]"
        role="status"
      >
        <svg
          v-if="t.variant === 'success'"
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round"
          class="shrink-0 mt-0.5"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg
          v-else-if="t.variant === 'error'"
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round"
          class="shrink-0 mt-0.5"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <svg
          v-else
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round"
          class="shrink-0 mt-0.5"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>

        <div class="min-w-0 flex-1">
          <p class="truncate">{{ t.message }}</p>
          <p v-if="t.detail" class="mt-0.5 text-xs font-normal opacity-80">
            {{ t.detail }}
          </p>
        </div>

        <button
          type="button"
          class="shrink-0 -mr-1 -mt-1 p-1 rounded
                 opacity-60 hover:opacity-100 transition-opacity"
          :aria-label="uebersetze('ui.schliessen')"
          @click="dismiss(t.id)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" aria-hidden="true"
               stroke-linecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </transition-group>
  </div>
</template>
